import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Layout } from '../components/layout';
import { Button } from '../components/ui';
import { api } from '../services/api';
import { useTelegram } from '../hooks/useTelegram';
import { Order } from '../types';
import { formatPrice } from '../utils/helpers';

export const PaymentPage: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { openLink } = useTelegram();

  const state = location.state as { paymentUrl?: string; stubPayAvailable?: boolean } | null;
  const passedUrl = state?.paymentUrl;
  const passedStub = state?.stubPayAvailable;

  const [paymentUrl, setPaymentUrl] = useState<string>(passedUrl || '');
  const [stubReady, setStubReady] = useState(!!passedStub);
  const [order, setOrder] = useState<Order | null>(null);
  const [isLoading, setIsLoading] = useState(!passedUrl && !passedStub);
  const [isPolling, setIsPolling] = useState(false);
  const [isStubbing, setIsStubbing] = useState(false);

  useEffect(() => {
    if (!orderId) {
      navigate('/');
      return;
    }

    if (!passedUrl) {
      loadOrder();
    } else {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    if (!orderId) return;
    setIsLoading(true);
    try {
      const o = await api.getOrder(orderId);
      if (o) {
        setOrder(o);
        if (o.paymentUrl && !paymentUrl) {
          setPaymentUrl(o.paymentUrl);
        }
        if (o.stubPayAvailable) {
          setStubReady(true);
        }
      }
    } catch { /* ignore */ }
    finally { setIsLoading(false); }
  };

  const pollStatus = useCallback(async () => {
    if (!orderId) return;
    try {
      const o = await api.getOrder(orderId);
      if (o) {
        setOrder(o);
        if (o.status === 'completed' || o.status === 'processing') {
          setIsPolling(false);
          navigate(`/order-status/${orderId}`, { replace: true });
        }
      }
    } catch { /* ignore */ }
  }, [orderId, navigate]);

  useEffect(() => {
    if (!isPolling) return;
    const interval = setInterval(pollStatus, 3000);
    return () => clearInterval(interval);
  }, [isPolling, pollStatus]);

  const handleOpenPayment = async () => {
    if (stubReady || order?.stubPayAvailable) {
      setIsStubbing(true);
      try {
        await api.stubPayOrder(orderId!);
        navigate(`/order-status/${orderId}`, { replace: true });
      } catch {
        setIsStubbing(false);
      }
      return;
    }
    if (paymentUrl) {
      openLink(paymentUrl);
      setIsPolling(true);
    }
  };

  const handleCheckStatus = () => {
    if (orderId) {
      navigate(`/order-status/${orderId}`);
    }
  };

  if (isLoading) {
    return (
      <Layout showNav={false}>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout showNav={false}>
      <div className="min-h-screen flex flex-col items-center justify-center px-5">
        <div className="max-w-md w-full">
          {/* Icon */}
          <div className="mb-6 flex justify-center">
            <div className="h-20 w-20 rounded-2xl bg-primary-50 flex items-center justify-center">
              <svg
                className="h-10 w-10 text-primary"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1.8}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                />
              </svg>
            </div>
          </div>

          {/* Header */}
          <h1 className="text-xl font-bold text-text-primary text-center mb-1.5">
            Переходим к оплате
          </h1>
          <p className="text-sm text-text-secondary text-center mb-2">
            Нажмите кнопку ниже, чтобы открыть платежную форму
          </p>
          {order && (
            <p className="text-sm text-text-secondary text-center mb-8">
              Сумма: <span className="font-bold text-text-primary">{formatPrice(order.totalAmount)}</span>
            </p>
          )}

          {/* Actions */}
          <div className="space-y-3">
            <Button
              onClick={handleOpenPayment}
              fullWidth
              size="lg"
              disabled={!paymentUrl && !stubReady && !order?.stubPayAvailable}
            >
              {isStubbing ? 'Обработка...' : isPolling ? 'Ожидание оплаты...' : 'Оплатить'}
            </Button>
            <Button
              onClick={handleCheckStatus}
              variant="secondary"
              fullWidth
              size="lg"
            >
              Проверить статус
            </Button>
          </div>

          {isPolling && (
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-text-secondary">
              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              Ожидаем подтверждение оплаты...
            </div>
          )}

          {/* Info */}
          <div className="mt-8 rounded-card p-4" style={{ backgroundColor: '#E8F5E9' }}>
            <div className="flex items-start">
              <svg
                className="w-4 h-4 mr-2.5 mt-0.5 flex-shrink-0"
                fill="#2E7D32"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <div className="text-sm" style={{ color: '#2E7D32' }}>
                <p>
                  Рекомендуем перед оплатой выключить VPN — так платеж пройдет быстрее.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};
